import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import { useGetWishlistQuery } from "../../api/wishlistApi";
import { useGetProductsByIdsQuery } from "../../../product/api/productApi";
import { sortProductsByIds } from "../../helpers/sortProductsByIds";
import WishlistTable from "./WishlistTable";
import EmptyState from "../../../cart/components/common/EmptyState";
import PageFeaturedProducts from "../../../featured/components/Page/PageFeaturedProducts";

const WishlistPageItemSkeleton: React.FC = () => {
  return (
    <div className="wishlist-page-item">
      {/* Cell 1: Remove Button */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--remove">
        <Skeleton circle width={36} height={36} />
      </div>

      {/* Cell 2: Image */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--image">
        <Skeleton style={{ aspectRatio: "1/1", display: "block" }} />
      </div>

      {/* Cell 3: Product Name */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--name">
        <Skeleton width="80%" count={2} />
      </div>

      {/* Cell 4: Price */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--price">
        <Skeleton width={80} />
      </div>

      {/* Cell 5: Stock Status */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--stock">
        <Skeleton width={60} />
      </div>

      {/* Cell 6: Actions */}
      <div className="wishlist-page-item__cell wishlist-page-item__cell--actions">
        <Skeleton height={40} width="100%" />
      </div>
    </div>
  );
};

const WishlistPageSkeleton: React.FC = () => (
  <div className="wishlist-page">
    <h1 className="wishlist-page__title">
      <Skeleton width={200} />
    </h1>
    <div className="wishlist-table">
      <div className="wishlist-table__body">
        {Array.from({ length: 3 }).map((_, i) => (
          <WishlistPageItemSkeleton key={i} />
        ))}
      </div>
    </div>
  </div>
);

const WishlistPage: React.FC = () => {
  const { t, ready: areTranslationsReady } = useTranslation();

  // 1. Get Wishlist IDs
  const {
    data: itemIds,
    isLoading: isWishlistLoading,
    isError: isWishlistError,
  } = useGetWishlistQuery();

  // 2. Fetch Products based on IDs
  // Skip if no IDs to avoid empty API call
  const shouldFetchProducts = !!itemIds && itemIds.length > 0;
  const {
    data: products,
    isLoading: areProductsLoading,
    isError: areProductsError,
  } = useGetProductsByIdsQuery(itemIds || [], {
    skip: !shouldFetchProducts,
  });

  const isComponentLoading =
    isWishlistLoading ||
    (shouldFetchProducts && areProductsLoading) ||
    !areTranslationsReady;
  const isError = isWishlistError || areProductsError;

  // Sort products to match the order of itemIds (LIFO usually)
  const sortedProducts = useMemo(() => {
    return sortProductsByIds(products, itemIds);
  }, [itemIds, products]);

  // State 1: Loading
  if (isComponentLoading) {
    return <WishlistPageSkeleton />;
  }

  // State 2: Error
  if (isError) {
    return (
      <div className="wishlist-page">
        <div className="wishlist-page__message wishlist-page__message--error">
          <p>
            {t(
              "wishlistPage.errorLoadingWishlist",
              "Error loading wishlist. Please try again."
            )}
          </p>
        </div>
      </div>
    );
  }

  // State 3: isFetching (We intentionally ignore this state to avoid flicker)

  // State 4: Success but no products
  if (!itemIds || itemIds.length === 0 || sortedProducts.length === 0) {
    return (
      <div className="wishlist-page wishlist-page--empty">
        <EmptyState
          iconName="heart-outline"
          title={t("wishlistPage.emptyTitle", "Your Wishlist is Empty")}
          message={t(
            "wishlistPage.emptyMessage",
            "To add products to your wishlist, click the heart icon on the product details page."
          )}
          buttonText={t("wishlistPage.continueShopping", "Continue Shopping")}
          buttonUrl={window.commonVariables?.shopUrl || "/"}
        >
          {/* Featured Content for Empty State */}
          <PageFeaturedProducts />
        </EmptyState>
      </div>
    );
  }

  // State 5: Success with products (Happy path)
  return (
    <div className="wishlist-page">
      <h1 className="wishlist-page__title">
        {t("wishlistPage.title", "Wishlist")}
      </h1>
      <WishlistTable products={sortedProducts} />
    </div>
  );
};

export default WishlistPage;
